"use client";

import { useState } from "react";
import Image from "next/image";
import { AlertCircle } from "lucide-react";
import { Label } from "@/components/ui/label";
import { CampaignPrerequisites } from "@/components/prerequisites/CampaignPrerequisites";
import { VISIBLE_CHAINS, getChainOption } from "@/lib/chain-config";
import { useSubscription } from "@/hooks/useSubscription";
import type { CampaignFormData, TenantColors } from "./types";
import type { TemplateInfo, CampaignPrerequisiteConfig } from "@/lib/claims/eligibility-types";

interface DigitalAssetsStepProps {
  campaignData: CampaignFormData;
  onCampaignChange: (field: keyof CampaignFormData, value: string | boolean) => void;
  onPrerequisitesChange: (config: CampaignPrerequisiteConfig) => void;
  availablePrerequisiteTemplates: TemplateInfo[];
  templatesLoading: boolean;
  isEditing?: boolean;
  tenantColors?: TenantColors | null;
}

const PLAN_LEVELS: Record<string, number> = { FREE: 0, STARTER: 1, PREMIUM: 2 };

export function DigitalAssetsStep({
  campaignData,
  onCampaignChange,
  onPrerequisitesChange,
  availablePrerequisiteTemplates,
  templatesLoading,
  isEditing,
}: DigitalAssetsStepProps) {
  const { subscription } = useSubscription();
  const currentPlan = subscription?.plan || "FREE";
  const currentPlanLevel = PLAN_LEVELS[currentPlan] || 0;
  // Toggled when the user taps the POAP warning icon. Keyed by chain value
  // so adding a second warning tile later would be trivial.
  const [openInfoFor, setOpenInfoFor] = useState<string | null>(null);

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900">Digital Assets</h2>
      <p className="text-sm text-gray-500">
        Configure the blockchain network and any prerequisites for digital asset rewards in this campaign.
      </p>

      <div>
        <Label>Blockchain Network</Label>
        {isEditing ? (
          <>
            <div className="mt-2 flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center">
                {campaignData.chain === 'WAX' ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                )}
              </div>
              <div>
                <span className="text-sm font-semibold text-gray-900">{getChainOption(campaignData.chain)?.label || campaignData.chain}</span>
                <p className="text-xs text-gray-500">{getChainOption(campaignData.chain)?.description || ''}</p>
              </div>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              The blockchain network cannot be changed after creation. To use a different network, create a new campaign.
            </p>
          </>
        ) : (
          <>
            <p className="text-xs text-gray-500 mt-1 mb-3">
              Select the blockchain network for minting digital assets in this campaign.
            </p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {VISIBLE_CHAINS.map((chain) => {
                const isSelected = campaignData.chain === chain.value;
                const requiredLevel = chain.requiredPlan ? PLAN_LEVELS[chain.requiredPlan] || 0 : 0;
                const isLocked = requiredLevel > currentPlanLevel;

                return (
                  <button
                    key={chain.value}
                    type="button"
                    onClick={() => !isLocked && onCampaignChange("chain", chain.value)}
                    disabled={isLocked}
                    className={`
                      relative flex flex-col items-center p-4 text-sm font-medium rounded-lg border-2 transition-all
                      ${isLocked
                        ? 'border-gray-100 bg-gray-50 text-gray-400 opacity-60 cursor-not-allowed'
                        : isSelected
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                      }
                    `}
                  >
                    {isLocked && (
                      <div className="absolute top-2 right-2">
                        <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}

                    {chain.value === 'POAP' && (
                      <>
                        <span
                          role="button"
                          tabIndex={0}
                          aria-label="POAP availability restrictions"
                          aria-expanded={openInfoFor === 'POAP'}
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenInfoFor(openInfoFor === 'POAP' ? null : 'POAP');
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              e.stopPropagation();
                              setOpenInfoFor(openInfoFor === 'POAP' ? null : 'POAP');
                            }
                          }}
                          className="absolute top-2 left-2 text-amber-500 hover:text-amber-600 cursor-pointer"
                        >
                          <AlertCircle className="w-4 h-4" />
                        </span>
                        {openInfoFor === 'POAP' && (
                          <span
                            role="tooltip"
                            onClick={(e) => e.stopPropagation()}
                            className="absolute top-8 left-2 right-2 z-10 rounded-md bg-gray-900 p-3 text-[11px] font-normal leading-snug text-white text-left shadow-lg cursor-default"
                          >
                            POAP is only available for issuer accounts created
                            before March 16, 2026. POAP has disabled the drop
                            feature for all new users.
                          </span>
                        )}
                      </>
                    )}

                    <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 overflow-hidden ${isLocked ? 'grayscale' : ''}`}>
                      <Image
                        src={chain.logo}
                        alt={`${chain.label} logo`}
                        width={40}
                        height={40}
                        className="object-contain"
                      />
                    </div>

                    <span className="text-xs font-semibold">{chain.label}</span>
                    <span className="text-[11px] text-gray-500 mt-0.5 text-center leading-tight">{chain.description}</span>

                    {isLocked ? (
                      <span className="mt-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-500 border border-gray-200">
                        Requires {chain.requiredPlan}
                      </span>
                    ) : isSelected ? (
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      {campaignData.chain !== 'POAP' && (
        <div>
          {currentPlanLevel < PLAN_LEVELS.STARTER ? (
            <div className="border border-gray-200 rounded-lg p-5">
              <h3 className="text-lg font-semibold text-gray-900">Campaign Prerequisites</h3>
              <p className="mt-2 text-sm text-gray-500">
                Require participants to own specific digital assets before claiming.
              </p>
              <div className="mt-3 inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
                <svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                Requires Starter plan
              </div>
            </div>
          ) : templatesLoading ? (
            <div className="space-y-4">
              <div className="border-b border-gray-200 pb-4">
                <h3 className="text-lg font-semibold text-gray-900">Campaign Prerequisites</h3>
                <p className="mt-1 text-sm text-gray-500">Loading templates...</p>
              </div>
              <div className="animate-pulse flex space-x-4">
                <div className="h-4 bg-gray-200 rounded w-1/4"></div>
              </div>
            </div>
          ) : (
            <CampaignPrerequisites
              config={campaignData.prerequisites || { templateIds: [], logicType: "AND", enabled: false }}
              onConfigChange={onPrerequisitesChange}
              availableTemplates={availablePrerequisiteTemplates}
            />
          )}
        </div>
      )}
    </div>
  );
}
